import React from "react";
import { BuildingIcon, UserIcon } from "../Icons";
import "./Charts.css";

const WIDTH = 720;
const HEIGHT = 500;
const CX = WIDTH / 2;
const CY = HEIGHT / 2;
const RADIUS = 150;
const NODE_R = 21;
const CENTER_R = 32;

export const RISK_COLORS = {
  High: "#df3f45",
  Medium: "#ef9c17",
  Low: "#26a269",
  Unknown: "#9aa7bb",
};

const NODE_ICONS = { company: BuildingIcon, person: UserIcon };

/**
 * Radial entity map: the subject in the middle, related parties on a ring.
 * Each node's `angle` (degrees, 0 = right, clockwise) fixes its position, so
 * the layout is stable rather than re-randomised on every render.
 */
export default function EntityGraph({ center, nodes, depthNote }) {
  const CenterIcon = NODE_ICONS[center.type] ?? BuildingIcon;

  const placed = nodes.map((node) => {
    const radians = (node.angle * Math.PI) / 180;
    return {
      ...node,
      x: CX + Math.cos(radians) * RADIUS,
      y: CY + Math.sin(radians) * RADIUS,
      // Labels sit outside the ring, away from the centre.
      anchor: Math.cos(radians) > 0.25 ? "start" : Math.cos(radians) < -0.25 ? "end" : "middle",
    };
  });

  const labelOffset = (node) => {
    if (node.anchor === "start") return NODE_R + 8;
    if (node.anchor === "end") return -(NODE_R + 8);
    return 0;
  };

  const labelTop = (node) => {
    if (node.anchor !== "middle") return node.y - 8;
    return node.y > CY ? node.y + NODE_R + 18 : node.y - NODE_R - 30;
  };

  return (
    <div className="egraph">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="egraph__svg"
        role="img"
        aria-label={`Relationship map for ${center.name}: ${nodes
          .map((node) => `${node.name}, ${node.risk} risk, ${node.link}`)
          .join("; ")}`}
      >
        {/* Edges first so nodes paint over their ends. */}
        {placed.map((node) => (
          <line
            key={`edge-${node.id}`}
            x1={CX}
            y1={CY}
            x2={node.x}
            y2={node.y}
            className={`egraph__edge egraph__edge--${node.link}`}
          />
        ))}

        {/* Centre node */}
        <circle cx={CX} cy={CY} r={CENTER_R + 7} className="egraph__halo" />
        <circle
          cx={CX}
          cy={CY}
          r={CENTER_R}
          className="egraph__node"
          stroke="#1657d0"
          strokeWidth="2.5"
        />
        <g transform={`translate(${CX - 14}, ${CY - 15})`}>
          <CenterIcon size={28} color="#1657d0" />
        </g>

        <text x={CX} y={CY + CENTER_R + 22} className="egraph__name" textAnchor="middle">
          {center.name}
        </text>
        <text x={CX} y={CY + CENTER_R + 38} className="egraph__nif" textAnchor="middle">
          NIF: {center.nif}
        </text>

        {/* Peripheral nodes */}
        {placed.map((node) => {
          const Icon = NODE_ICONS[node.type] ?? BuildingIcon;
          const color = RISK_COLORS[node.risk];
          const dx = labelOffset(node);
          const top = labelTop(node);

          return (
            <g key={node.id}>
              <circle
                cx={node.x}
                cy={node.y}
                r={NODE_R}
                className="egraph__node"
                stroke={color}
                strokeWidth="2"
              />
              <g transform={`translate(${node.x - 9}, ${node.y - 9})`}>
                <Icon size={18} color={color} />
              </g>

              <text
                x={node.x + dx}
                y={top}
                className="egraph__name"
                textAnchor={node.anchor}
              >
                {node.name}
              </text>
              <text
                x={node.x + dx}
                y={top + 15}
                className="egraph__nif"
                textAnchor={node.anchor}
              >
                NIF: {node.nif}
              </text>
              <text
                x={node.x + dx}
                y={top + 30}
                className="egraph__risk"
                textAnchor={node.anchor}
                fill={color}
              >
                {node.risk} Risk
              </text>
            </g>
          );
        })}
      </svg>

      {depthNote && <p className="egraph__note">{depthNote}</p>}
    </div>
  );
}
